import { FC, useEffect, useState } from "react";

import {
  Alert,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  deleteGeoDataFile,
  GeoDataFileModel,
  GeoDataFileProcessingRunModel,
  getGeoDataFile,
  listGeoDataFileProcessingRun,
  postGeoDataFileProcess,
  ProcessingOptions,
  SingleMetadataMode,
} from "@rubi/api-generated";
import { useNavigate, useParams } from "react-router-dom";

import { Crumb, DevPage, ErrorMsg, Section } from "./common";
import { onRowActivateKeyDown, RegionBreadcrumbs } from "./regionShared";

type Mode = "single" | "group_by";

const runColumns: GridColDef<GeoDataFileProcessingRunModel>[] = [
  { field: "id", headerName: "ID", flex: 1, minWidth: 280 },
  {
    field: "mode",
    headerName: "Mode",
    width: 120,
    valueGetter: (_value, row) => row.options.mode,
  },
  { field: "status", headerName: "Status", width: 120 },
  {
    field: "time_created",
    headerName: "Time Created",
    width: 200,
    valueFormatter: (value: string) => new Date(value).toLocaleString(),
  },
];

export const DevGeoDataFilePage: FC = () => {
  const { orgId = "", fileId = "" } = useParams();
  const navigate = useNavigate();

  const [file, setFile] = useState<GeoDataFileModel | null>(null);
  const [runs, setRuns] = useState<GeoDataFileProcessingRunModel[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<Mode>("single");
  const [metadataMode, setMetadataMode] = useState<SingleMetadataMode>("merge");
  const [groupByProperty, setGroupByProperty] = useState("");
  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const loadRuns = async () => {
    setRunsLoading(true);
    const response = await listGeoDataFileProcessingRun({
      path: { org_id: orgId },
      query: { geo_data_file_id: fileId },
      throwOnError: false,
    });
    if (response.status === 200) {
      setRuns(response.data?.geo_data_file_processing_runs ?? []);
    }
    setRunsLoading(false);
  };

  useEffect(() => {
    getGeoDataFile({ path: { org_id: orgId, file_id: fileId } })
      .then((response) => setFile(response.data ?? null))
      .catch((e) =>
        setError(e instanceof Error ? e.message : "Failed to load file"),
      );
    loadRuns();
  }, [orgId, fileId]);

  const openRun = (runId: string) =>
    navigate(`/dev_admin/region/${orgId}/geo_data_file/${fileId}/run/${runId}`);

  const canProcess =
    file?.status === "uploaded" &&
    !processing &&
    (mode === "single" || groupByProperty.trim().length > 0);

  const handleProcess = async () => {
    setProcessing(true);
    setProcessError(null);
    try {
      const options: ProcessingOptions =
        mode === "single"
          ? { mode: "single", metadata: metadataMode }
          : { mode: "group_by", property: groupByProperty.trim() };

      const response = await postGeoDataFileProcess({
        path: { org_id: orgId, file_id: fileId },
        body: { options },
      });
      const run = response.data;
      if (!run) throw new Error("No run returned");
      navigate(
        `/dev_admin/region/${orgId}/geo_data_file/${fileId}/run/${run.id}`,
      );
    } catch (e) {
      setProcessError(
        e instanceof Error ? e.message : "Failed to start processing",
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    const name = file?.filename ?? fileId;
    if (!window.confirm(`Delete GeoDataFile ${name}? This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteGeoDataFile({ path: { org_id: orgId, file_id: fileId } });
      navigate(`/dev_admin/region/${orgId}/geo_data_file`);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "Failed to delete");
      setDeleting(false);
    }
  };

  return (
    <DevPage
      title={file?.filename ?? fileId}
      breadcrumbs={
        <RegionBreadcrumbs orgId={orgId}>
          <Crumb>{file?.filename ?? fileId}</Crumb>
        </RegionBreadcrumbs>
      }
    >
      <Section subtitle="Details">
        <ErrorMsg msg={error} />
        <table className="dev-table dev-table--details">
          <tbody>
            <tr>
              <td>id</td>
              <td className="mono">{file?.id}</td>
            </tr>
            <tr>
              <td>status</td>
              <td>{file?.status}</td>
            </tr>
            <tr>
              <td>content_type</td>
              <td>{file?.content_type}</td>
            </tr>
            <tr>
              <td>size_bytes</td>
              <td className="mono">{file?.size_bytes.toLocaleString()}</td>
            </tr>
            <tr>
              <td>time_created</td>
              <td className="mono muted">{file?.time_created}</td>
            </tr>
          </tbody>
        </table>

        {deleteError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteError}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete file"}
          </Button>
        </Stack>
      </Section>

      <Section subtitle="Generate Subregions">
        {file?.status !== "uploaded" ? (
          <Typography color="text.secondary">
            Waiting for the upload to complete before this file can be
            processed.
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ maxWidth: 480 }}>
            <TextField
              select
              label="Mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as Mode)}
            >
              <MenuItem value="single">
                Single — merge every feature into one Subregion
              </MenuItem>
              <MenuItem value="group_by">
                Group by property — one Subregion per distinct value
              </MenuItem>
            </TextField>

            {mode === "single" ? (
              <TextField
                select
                label="Metadata"
                value={metadataMode}
                onChange={(e) =>
                  setMetadataMode(e.target.value as SingleMetadataMode)
                }
                helperText="Whether to merge the source features' properties into the resulting Subregion's metadata"
              >
                <MenuItem value="merge">Merge feature properties</MenuItem>
                <MenuItem value="none">No metadata</MenuItem>
              </TextField>
            ) : (
              <TextField
                label="Group-by property"
                value={groupByProperty}
                onChange={(e) => setGroupByProperty(e.target.value)}
                helperText='The GeoJSON feature property to group on, e.g. "region"'
              />
            )}

            {processError && <Alert severity="error">{processError}</Alert>}

            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleProcess}
                disabled={!canProcess}
              >
                {processing ? "Starting…" : "Generate Subregions"}
              </Button>
            </Stack>
          </Stack>
        )}
      </Section>

      <Section subtitle="Processing runs">
        <div style={{ width: "100%" }}>
          <DataGrid
            rows={runs}
            columns={runColumns}
            loading={runsLoading}
            autoHeight
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50]}
            onRowClick={(params) => openRun(String(params.id))}
            onCellKeyDown={onRowActivateKeyDown(openRun)}
            sx={{ cursor: "pointer" }}
          />
        </div>
      </Section>
    </DevPage>
  );
};
